/**
 * Font Database
 *
 * Static lookup table mapping common fonts to Google Fonts alternatives.
 * Used by the design system detection script to find suitable fallbacks
 * for fonts that aren't freely available.
 *
 * @module fonts/font-database
 */

import { z } from 'zod';

/**
 * Font category classification
 */
export const FontCategorySchema = z.enum([
  'sans-serif',
  'serif',
  'display',
  'handwriting',
  'monospace',
]);

export type FontCategory = z.infer<typeof FontCategorySchema>;

/**
 * Font style sub-classification for more precise matching
 */
export const FontStyleSchema = z.enum([
  // Sans-serif styles
  'geometric', // Futura, Avenir, Montserrat
  'grotesque', // Helvetica, Arial, Inter
  'humanist', // Open Sans, Lato, Source Sans
  'neo-grotesque', // Roboto, SF Pro, Segoe UI

  // Serif styles
  'old-style', // Garamond, Palatino
  'transitional', // Times, Georgia, Baskerville
  'modern', // Didot, Bodoni, Playfair Display
  'slab', // Rockwell, Roboto Slab

  // Other
  'display', // Decorative fonts
  'script', // Cursive, handwriting
  'monospace', // Fixed-width
]);

export type FontStyle = z.infer<typeof FontStyleSchema>;

/**
 * Font entry in the database
 */
export interface FontEntry {
  /** Font family name */
  name: string;
  /** Primary classification */
  category: FontCategory;
  /** Sub-classification for matching */
  style: FontStyle;
  /** Google Fonts family name (if available) */
  googleFontsId?: string;
  /** Whether this is a system font */
  isSystemFont: boolean;
  /** Alternative names this font may be known by */
  aliases: string[];
  /** Font weights typically available */
  weights: number[];
}

/**
 * Google Fonts available in the system
 *
 * These fonts are freely available via Google Fonts and can be used directly.
 */
export const GOOGLE_FONTS: FontEntry[] = [
  // === SANS-SERIF: Geometric ===
  {
    name: 'Montserrat',
    category: 'sans-serif',
    style: 'geometric',
    googleFontsId: 'Montserrat',
    isSystemFont: false,
    aliases: [],
    weights: [100, 200, 300, 400, 500, 600, 700, 800, 900],
  },
  {
    name: 'Poppins',
    category: 'sans-serif',
    style: 'geometric',
    googleFontsId: 'Poppins',
    isSystemFont: false,
    aliases: [],
    weights: [100, 200, 300, 400, 500, 600, 700, 800, 900],
  },
  {
    name: 'Nunito',
    category: 'sans-serif',
    style: 'geometric',
    googleFontsId: 'Nunito',
    isSystemFont: false,
    aliases: ['Nunito Sans'],
    weights: [200, 300, 400, 500, 600, 700, 800, 900],
  },
  {
    name: 'Josefin Sans',
    category: 'sans-serif',
    style: 'geometric',
    googleFontsId: 'Josefin+Sans',
    isSystemFont: false,
    aliases: [],
    weights: [100, 200, 300, 400, 500, 600, 700],
  },
  {
    name: 'Quicksand',
    category: 'sans-serif',
    style: 'geometric',
    googleFontsId: 'Quicksand',
    isSystemFont: false,
    aliases: [],
    weights: [300, 400, 500, 600, 700],
  },

  // === SANS-SERIF: Grotesque/Neo-Grotesque ===
  {
    name: 'Inter',
    category: 'sans-serif',
    style: 'grotesque',
    googleFontsId: 'Inter',
    isSystemFont: false,
    aliases: [],
    weights: [100, 200, 300, 400, 500, 600, 700, 800, 900],
  },
  {
    name: 'Roboto',
    category: 'sans-serif',
    style: 'neo-grotesque',
    googleFontsId: 'Roboto',
    isSystemFont: false,
    aliases: [],
    weights: [100, 300, 400, 500, 700, 900],
  },
  {
    name: 'DM Sans',
    category: 'sans-serif',
    style: 'grotesque',
    googleFontsId: 'DM+Sans',
    isSystemFont: false,
    aliases: [],
    weights: [400, 500, 700],
  },
  {
    name: 'Work Sans',
    category: 'sans-serif',
    style: 'grotesque',
    googleFontsId: 'Work+Sans',
    isSystemFont: false,
    aliases: [],
    weights: [100, 200, 300, 400, 500, 600, 700, 800, 900],
  },
  {
    name: 'Barlow',
    category: 'sans-serif',
    style: 'grotesque',
    googleFontsId: 'Barlow',
    isSystemFont: false,
    aliases: ['Barlow Condensed', 'Barlow Semi Condensed'],
    weights: [100, 200, 300, 400, 500, 600, 700, 800, 900],
  },

  // === SANS-SERIF: Humanist ===
  {
    name: 'Open Sans',
    category: 'sans-serif',
    style: 'humanist',
    googleFontsId: 'Open+Sans',
    isSystemFont: false,
    aliases: [],
    weights: [300, 400, 500, 600, 700, 800],
  },
  {
    name: 'Lato',
    category: 'sans-serif',
    style: 'humanist',
    googleFontsId: 'Lato',
    isSystemFont: false,
    aliases: [],
    weights: [100, 300, 400, 700, 900],
  },
  {
    name: 'Source Sans Pro',
    category: 'sans-serif',
    style: 'humanist',
    googleFontsId: 'Source+Sans+Pro',
    isSystemFont: false,
    aliases: ['Source Sans 3'],
    weights: [200, 300, 400, 600, 700, 900],
  },
  {
    name: 'Mulish',
    category: 'sans-serif',
    style: 'humanist',
    googleFontsId: 'Mulish',
    isSystemFont: false,
    aliases: [],
    weights: [200, 300, 400, 500, 600, 700, 800, 900],
  },
  {
    name: 'Raleway',
    category: 'sans-serif',
    style: 'humanist',
    googleFontsId: 'Raleway',
    isSystemFont: false,
    aliases: [],
    weights: [100, 200, 300, 400, 500, 600, 700, 800, 900],
  },

  // === SERIF: Modern ===
  {
    name: 'Playfair Display',
    category: 'serif',
    style: 'modern',
    googleFontsId: 'Playfair+Display',
    isSystemFont: false,
    aliases: [],
    weights: [400, 500, 600, 700, 800, 900],
  },
  {
    name: 'Bodoni Moda',
    category: 'serif',
    style: 'modern',
    googleFontsId: 'Bodoni+Moda',
    isSystemFont: false,
    aliases: [],
    weights: [400, 500, 600, 700, 800, 900],
  },

  // === SERIF: Transitional ===
  {
    name: 'Merriweather',
    category: 'serif',
    style: 'transitional',
    googleFontsId: 'Merriweather',
    isSystemFont: false,
    aliases: [],
    weights: [300, 400, 700, 900],
  },
  {
    name: 'Lora',
    category: 'serif',
    style: 'transitional',
    googleFontsId: 'Lora',
    isSystemFont: false,
    aliases: [],
    weights: [400, 500, 600, 700],
  },
  {
    name: 'Source Serif Pro',
    category: 'serif',
    style: 'transitional',
    googleFontsId: 'Source+Serif+Pro',
    isSystemFont: false,
    aliases: ['Source Serif 4'],
    weights: [200, 300, 400, 600, 700, 900],
  },
  {
    name: 'Libre Baskerville',
    category: 'serif',
    style: 'transitional',
    googleFontsId: 'Libre+Baskerville',
    isSystemFont: false,
    aliases: [],
    weights: [400, 700],
  },
  {
    name: 'Crimson Text',
    category: 'serif',
    style: 'transitional',
    googleFontsId: 'Crimson+Text',
    isSystemFont: false,
    aliases: ['Crimson Pro'],
    weights: [400, 600, 700],
  },

  // === SERIF: Old Style ===
  {
    name: 'EB Garamond',
    category: 'serif',
    style: 'old-style',
    googleFontsId: 'EB+Garamond',
    isSystemFont: false,
    aliases: [],
    weights: [400, 500, 600, 700, 800],
  },
  {
    name: 'Cormorant Garamond',
    category: 'serif',
    style: 'old-style',
    googleFontsId: 'Cormorant+Garamond',
    isSystemFont: false,
    aliases: ['Cormorant'],
    weights: [300, 400, 500, 600, 700],
  },
  {
    name: 'Libre Caslon Text',
    category: 'serif',
    style: 'old-style',
    googleFontsId: 'Libre+Caslon+Text',
    isSystemFont: false,
    aliases: [],
    weights: [400, 700],
  },

  // === SERIF: Slab ===
  {
    name: 'Roboto Slab',
    category: 'serif',
    style: 'slab',
    googleFontsId: 'Roboto+Slab',
    isSystemFont: false,
    aliases: [],
    weights: [100, 200, 300, 400, 500, 600, 700, 800, 900],
  },
  {
    name: 'Arvo',
    category: 'serif',
    style: 'slab',
    googleFontsId: 'Arvo',
    isSystemFont: false,
    aliases: [],
    weights: [400, 700],
  },

  // === MONOSPACE ===
  {
    name: 'Fira Code',
    category: 'monospace',
    style: 'monospace',
    googleFontsId: 'Fira+Code',
    isSystemFont: false,
    aliases: ['Fira Mono'],
    weights: [300, 400, 500, 600, 700],
  },
  {
    name: 'JetBrains Mono',
    category: 'monospace',
    style: 'monospace',
    googleFontsId: 'JetBrains+Mono',
    isSystemFont: false,
    aliases: [],
    weights: [100, 200, 300, 400, 500, 600, 700, 800],
  },
  {
    name: 'Source Code Pro',
    category: 'monospace',
    style: 'monospace',
    googleFontsId: 'Source+Code+Pro',
    isSystemFont: false,
    aliases: [],
    weights: [200, 300, 400, 500, 600, 700, 900],
  },
];

/**
 * Common proprietary fonts that need to be mapped to Google Fonts alternatives
 */
export const PROPRIETARY_FONTS: FontEntry[] = [
  // === Proprietary Sans-Serif: Geometric ===
  {
    name: 'Futura',
    category: 'sans-serif',
    style: 'geometric',
    isSystemFont: false,
    aliases: ['Futura PT', 'Futura Std', 'Futura Book'],
    weights: [300, 400, 500, 700],
  },
  {
    name: 'Avenir',
    category: 'sans-serif',
    style: 'geometric',
    isSystemFont: false,
    aliases: ['Avenir Next', 'Avenir LT Std'],
    weights: [300, 400, 500, 600, 700, 800, 900],
  },
  {
    name: 'Proxima Nova',
    category: 'sans-serif',
    style: 'geometric',
    isSystemFont: false,
    aliases: ['Proxima Nova Soft', 'proxima-nova'],
    weights: [100, 300, 400, 500, 600, 700, 800, 900],
  },
  {
    name: 'Gotham',
    category: 'sans-serif',
    style: 'geometric',
    isSystemFont: false,
    aliases: ['Gotham Rounded', 'Gotham SSm'],
    weights: [300, 400, 500, 700, 800, 900],
  },
  {
    name: 'Brandon Grotesque',
    category: 'sans-serif',
    style: 'geometric',
    isSystemFont: false,
    aliases: ['Brandon Text'],
    weights: [300, 400, 500, 700, 900],
  },
  {
    name: 'Circular',
    category: 'sans-serif',
    style: 'geometric',
    isSystemFont: false,
    aliases: ['Circular Std', 'LL Circular'],
    weights: [300, 400, 500, 700, 900],
  },

  // === Proprietary Sans-Serif: Grotesque ===
  {
    name: 'Helvetica',
    category: 'sans-serif',
    style: 'grotesque',
    isSystemFont: true,
    aliases: ['Helvetica Neue', 'Helvetica Now'],
    weights: [300, 400, 500, 700],
  },
  {
    name: 'Arial',
    category: 'sans-serif',
    style: 'grotesque',
    isSystemFont: true,
    aliases: [],
    weights: [400, 700],
  },
  {
    name: 'SF Pro',
    category: 'sans-serif',
    style: 'neo-grotesque',
    isSystemFont: true,
    aliases: ['SF Pro Display', 'SF Pro Text', '-apple-system'],
    weights: [100, 200, 300, 400, 500, 600, 700, 800, 900],
  },
  {
    name: 'Segoe UI',
    category: 'sans-serif',
    style: 'neo-grotesque',
    isSystemFont: true,
    aliases: [],
    weights: [300, 400, 600, 700],
  },

  // === Proprietary Sans-Serif: Humanist ===
  {
    name: 'Museo Sans',
    category: 'sans-serif',
    style: 'humanist',
    isSystemFont: false,
    aliases: ['Museo', 'museo-sans'],
    weights: [100, 300, 500, 700, 900],
  },
  {
    name: 'Din',
    category: 'sans-serif',
    style: 'humanist',
    isSystemFont: false,
    aliases: ['DIN', 'FF DIN', 'DIN Pro', 'DIN Next'],
    weights: [300, 400, 500, 700],
  },
  {
    name: 'Gill Sans',
    category: 'sans-serif',
    style: 'humanist',
    isSystemFont: true,
    aliases: ['Gill Sans MT', 'Gill Sans Nova'],
    weights: [300, 400, 600, 700],
  },

  // === Proprietary Serif: Modern ===
  {
    name: 'Didot',
    category: 'serif',
    style: 'modern',
    isSystemFont: true,
    aliases: ['Didot LT Std'],
    weights: [400, 700],
  },
  {
    name: 'Bodoni',
    category: 'serif',
    style: 'modern',
    isSystemFont: false,
    aliases: ['Bodoni 72', 'Bodoni MT'],
    weights: [400, 700],
  },

  // === Proprietary Serif: Transitional ===
  {
    name: 'Georgia',
    category: 'serif',
    style: 'transitional',
    isSystemFont: true,
    aliases: [],
    weights: [400, 700],
  },
  {
    name: 'Times New Roman',
    category: 'serif',
    style: 'transitional',
    isSystemFont: true,
    aliases: ['Times'],
    weights: [400, 700],
  },
  {
    name: 'Chronicle',
    category: 'serif',
    style: 'transitional',
    isSystemFont: false,
    aliases: ['Chronicle Display', 'Chronicle Text'],
    weights: [300, 400, 700],
  },
  {
    name: 'Mercury',
    category: 'serif',
    style: 'transitional',
    isSystemFont: false,
    aliases: ['Mercury Text', 'Mercury Display'],
    weights: [400, 700],
  },
  {
    name: 'Freight Text',
    category: 'serif',
    style: 'transitional',
    isSystemFont: false,
    aliases: ['Freight Display', 'Freight Sans'],
    weights: [300, 400, 500, 700],
  },

  // === Proprietary Serif: Old Style ===
  {
    name: 'Garamond',
    category: 'serif',
    style: 'old-style',
    isSystemFont: true,
    aliases: ['Adobe Garamond', 'Garamond Premier Pro', 'ITC Garamond'],
    weights: [400, 700],
  },
  {
    name: 'Caslon',
    category: 'serif',
    style: 'old-style',
    isSystemFont: false,
    aliases: ['Adobe Caslon', 'Big Caslon'],
    weights: [400, 700],
  },
  {
    name: 'Palatino',
    category: 'serif',
    style: 'old-style',
    isSystemFont: true,
    aliases: ['Palatino Linotype', 'Book Antiqua'],
    weights: [400, 700],
  },
  {
    name: 'Baskerville',
    category: 'serif',
    style: 'transitional',
    isSystemFont: true,
    aliases: ['Libre Baskerville'],
    weights: [400, 700],
  },
];

/**
 * All fonts in the database (Google Fonts + proprietary)
 */
export const ALL_FONTS: FontEntry[] = [...GOOGLE_FONTS, ...PROPRIETARY_FONTS];

/**
 * Quick lookup map: proprietary font name → recommended Google Fonts alternative
 *
 * Based on visual similarity (category, style, x-height, contrast).
 */
export const FONT_FALLBACK_MAP: Record<string, string> = {
  // Sans-serif geometric
  Futura: 'Poppins',
  'Futura PT': 'Poppins',
  Avenir: 'Nunito',
  'Avenir Next': 'Nunito',
  'Proxima Nova': 'Montserrat',
  Gotham: 'Poppins',
  'Brandon Grotesque': 'Josefin Sans',
  Circular: 'DM Sans',
  'Circular Std': 'DM Sans',

  // Sans-serif grotesque/neo-grotesque
  Helvetica: 'Inter',
  'Helvetica Neue': 'Inter',
  Arial: 'Inter',
  'SF Pro': 'Inter',
  'SF Pro Display': 'Inter',
  '-apple-system': 'Inter',
  'Segoe UI': 'Roboto',

  // Sans-serif humanist
  'Museo Sans': 'Mulish',
  Din: 'Barlow',
  'DIN Pro': 'Barlow',
  'FF DIN': 'Barlow',
  'Gill Sans': 'Lato',

  // Serif modern
  Didot: 'Playfair Display',
  Bodoni: 'Bodoni Moda',

  // Serif transitional
  Georgia: 'Merriweather',
  'Times New Roman': 'Merriweather',
  Times: 'Merriweather',
  Chronicle: 'Source Serif Pro',
  Mercury: 'Source Serif Pro',
  'Freight Text': 'Lora',

  // Serif old-style
  Garamond: 'EB Garamond',
  'Adobe Garamond': 'EB Garamond',
  Caslon: 'Libre Caslon Text',
  'Adobe Caslon': 'Libre Caslon Text',
  Palatino: 'Crimson Text',
  Baskerville: 'Libre Baskerville',
};

/**
 * Default fallback fonts by category
 */
export const DEFAULT_FALLBACKS: Record<FontCategory, string> = {
  'sans-serif': 'Inter',
  serif: 'Merriweather',
  display: 'Playfair Display',
  handwriting: 'Lora',
  monospace: 'Fira Code',
};
